import { ReactNode, Fragment } from 'react';

import ScrollToOnMount from 'approot/shared/scroll-to-on-mount/scroll-to-on-mount';
import { spacing4 } from 'approot/shared/styles/spacing.style';
import { NarrowerContainer } from './content-page-narrow-container';
import {
  ContentPageSectionHeader,
  ContentPageHeading18,
  ContentPageSection as ContentPageSectionStyled,
  ContentDivider,
} from './content-page.style';
import { Box } from 'approot/shared/styles/box.style';

type SectionTitleProps = JSX.IntrinsicElements['h2'] & {
  children?: ReactNode;
  prefixChildren?: ReactNode;
  postfixChildren?: ReactNode;
};

export const SectionTitle = ({
  children,
  prefixChildren,
  postfixChildren,
}: SectionTitleProps) => (
  <ContentPageSectionHeader>
    {prefixChildren && <>{prefixChildren}</>}
    <ContentPageHeading18
      data-selector="content-section-title"
      data-section-title
      marginBottom={0}
      tabIndex={-1}
    >
      {children}
    </ContentPageHeading18>
    {postfixChildren && <Box $marginLeft={8}>{postfixChildren}</Box>}
  </ContentPageSectionHeader>
);

export const ContentPageSection = ({
  children,
  title,
  hash,
  titleMarginBottom = spacing4,
  isFullWidth,
  hideContentDivider,
  marginBottom,
  ...rest
}: {
  children: ReactNode;
  title: string;
  hash: string;
  titleMarginBottom?: number;
  isFullWidth?: boolean;
  hideContentDivider?: boolean;
  marginBottom?: number;
}) => {
  const Container = isFullWidth ? Fragment : NarrowerContainer;

  return (
    <ScrollToOnMount hashPattern={hash}>
      <ContentPageSectionStyled
        data-nav-title={title}
        data-nav-anchor={hash}
        marginBottom={marginBottom}
        {...rest}
      >
        <Container>
          {!hideContentDivider && <ContentDivider />}
          <ContentPageHeading18
            marginBottom={titleMarginBottom}
            data-selector="content-section-title"
            data-section-title
            tabIndex={-1}
          >
            {title}
          </ContentPageHeading18>
          {children}
        </Container>
      </ContentPageSectionStyled>
    </ScrollToOnMount>
  );
};

export const ContentPageCardSection = ({
  children,
  title,
  titleBadge,
  hash,
  titleMarginBottom = spacing4,
  prefixChildren,
  ...rest
}: {
  children: ReactNode;
  title: string;
  titleBadge?: ReactNode;
  hash: string;
  titleMarginBottom?: number;
  prefixChildren?: ReactNode;
}) => (
  <ScrollToOnMount hashPattern={hash}>
    <ContentPageSectionStyled
      data-nav-title={title}
      data-nav-anchor={hash}
      {...rest}
    >
      <SectionTitle
        prefixChildren={prefixChildren}
        postfixChildren={titleBadge}
      >
        {title}
      </SectionTitle>
      {children}
    </ContentPageSectionStyled>
  </ScrollToOnMount>
);
